import { useState } from "react";
import { Check, ChevronsUpDown, Plus } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

export const Combobox = ({ value, onChange, options = [], placeholder = "Select or type…", testId, allowCreate = true }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const q = query.trim().toLowerCase();
  const filtered = q ? options.filter((o) => o.toLowerCase().includes(q)) : options;
  const exact = options.some((o) => o.toLowerCase() === q);

  const pick = (val) => {
    onChange(val);
    setOpen(false);
    setQuery("");
  };

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setQuery(""); }}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          data-testid={testId}
          className="mt-1 w-full justify-between rounded-md font-normal"
        >
          <span className={cn(!value && "text-slate-400")}>{value || placeholder}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <div className="p-2 border-b border-slate-100">
          <Input
            autoFocus
            data-testid={testId ? `${testId}-search` : undefined}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && allowCreate && query.trim() && !exact) {
                e.preventDefault();
                pick(query.trim());
              }
            }}
            placeholder="Search or add new…"
            className="h-9 rounded-md"
          />
        </div>
        <div className="max-h-56 overflow-y-auto py-1">
          {filtered.map((o) => (
            <button
              key={o}
              type="button"
              data-testid={testId ? `${testId}-option-${o}` : undefined}
              onClick={() => pick(o)}
              className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-slate-50"
            >
              <span className="truncate">{o}</span>
              {value === o && <Check className="h-4 w-4 text-slate-900" />}
            </button>
          ))}
          {allowCreate && query.trim() && !exact && (
            <button
              type="button"
              data-testid={testId ? `${testId}-create` : undefined}
              onClick={() => pick(query.trim())}
              className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-blue-600 hover:bg-blue-50"
            >
              <Plus className="h-4 w-4" /> Add "{query.trim()}"
            </button>
          )}
          {filtered.length === 0 && !query.trim() && (
            <div className="px-3 py-6 text-center text-sm text-slate-400">No vendors yet — type to add one.</div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};
